import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MainLayout } from '../../components/MainLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@werkstatt/ui';
import { Avatar, AvatarFallback } from '@werkstatt/ui';
import { User, Lock, Shield, Monitor } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import ProfileTab from './ProfileTab';
import SecurityTab from './SecurityTab';
import SessionsTab from './SessionsTab';
import TwoFactorTab from './TwoFactorTab';
import api from '../../lib/api';

interface Profile {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  role: string;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrator',
  user: 'Benutzer',
  super_admin: 'Superadministrator',
};

export default function Account() {
  const [activeTab, setActiveTab] = useState('profile');
  const { user } = useAuthStore();

  const { data: profile } = useQuery<Profile>({
    queryKey: ['account-profile'],
    queryFn: async () => (await api.get('/account/profile')).data,
  });

  const displayName =
    profile?.first_name || profile?.last_name
      ? `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim()
      : user?.email ?? '';

  const initials = displayName
    ? displayName
        .split(' ')
        .map((p) => p[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : (user?.email?.slice(0, 2).toUpperCase() ?? '??');

  return (
    <MainLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Kontoeinstellungen</h1>
          <p className="text-muted-foreground mt-1">
            Profil, Passwort und Sicherheitseinstellungen verwalten
          </p>
        </div>

        {/* Overview Card */}
        <div className="rounded-card backdrop-blur-lg shadow-glass bg-[rgb(var(--sphere-card)_/_var(--sphere-card-alpha))] border border-[rgb(var(--sphere-border)_/_var(--sphere-border-alpha))] p-5 flex items-center gap-4">
          <Avatar className="h-14 w-14 shrink-0">
            <AvatarFallback className="bg-primary text-primary-foreground text-lg font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="font-semibold text-lg leading-tight truncate">{displayName}</p>
            <p className="text-sm text-muted-foreground truncate">{profile?.email ?? user?.email}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {ROLE_LABELS[profile?.role ?? user?.role ?? ''] ?? profile?.role}
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="overflow-x-auto">
            <TabsList className="inline-flex min-w-full sm:grid sm:grid-cols-4">
              <TabsTrigger value="profile" className="flex-1 flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4">
                <User className="h-3.5 w-3.5 shrink-0" />
                Profil
              </TabsTrigger>
              <TabsTrigger value="security" className="flex-1 flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4">
                <Lock className="h-3.5 w-3.5 shrink-0" />
                Sicherheit
              </TabsTrigger>
              <TabsTrigger value="2fa" className="flex-1 flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4">
                <Shield className="h-3.5 w-3.5 shrink-0" />
                2-Faktor-Auth
              </TabsTrigger>
              <TabsTrigger value="sessions" className="flex-1 flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4">
                <Monitor className="h-3.5 w-3.5 shrink-0" />
                Sitzungen
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="profile" className="space-y-4">
            <ProfileTab />
          </TabsContent>

          <TabsContent value="security" className="space-y-4">
            <SecurityTab />
          </TabsContent>

          <TabsContent value="2fa" className="space-y-4">
            <TwoFactorTab />
          </TabsContent>

          <TabsContent value="sessions" className="space-y-4">
            <SessionsTab />
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
